// src/App.js
import React, { useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, Navigate } from 'react-router-dom';

import Encabezado from './componentes/Encabezado';
import PieDePagina from './componentes/PieDePagina';
import IniciarSesion from './paginas/IniciarSesion';
import SolicitarAcceso from './paginas/SolicitarAcceso';
import SolicitarRecuperacion from './paginas/SolicitarRecuperacion';
import RestablecerPassword from './paginas/RestablecerPassword';
import RutaPrivada from './componentes/RutaPrivada';
import ModalCrearAviso from './componentes/ModalCrearAviso';

// Importar el AuthProvider
import { AuthProvider, useAuth } from './contexto/AuthContext';
import { GoogleOAuthProvider } from '@react-oauth/google'; // Importar GoogleOAuthProvider

// Importar los componentes de Avisos
import AvisosDelDia from './paginas/AvisosDelDia';
import AvisosPendientes from './paginas/AvisosPendientes';
import AvisosCompletados from './paginas/AvisosCompletados';
import HistorialDeServicios from './paginas/HistorialDeServicios';

// Importar componentes de Finanzas, Stock y Clientes
import FinancialApp from './paginas/Finanzas';
import FacturacionSimple from './paginas/FacturacionSimple';
import ListaFacturas from './paginas/ListaFacturas';
import Inventario from './paginas/Inventario';
import VerClientePage from './paginas/VerClientePage';
import EditarClientePage from './paginas/EditarClientePage';
import GestionClientesPage from './paginas/GestionClientesPage';
import BuscarClientePage from './paginas/BuscarClientePage';
import DashboardPage from './paginas/DashboardPage';
import GestionTecnicosPage from './paginas/GestionTecnicosPage';


// Páginas placeholder para el módulo de Avisos
const CrearAvisos = ({ onOpenModal }) => {
  // Este componente ahora solo tiene un botón para abrir el modal
  return (
    <div className="flex flex-col items-center justify-center h-full">
      <h1 className="text-2xl font-bold mb-4">Crear Avisos</h1>
      <button
        onClick={onOpenModal}
        className="bg-orange-600 hover:bg-orange-700 text-white font-bold py-2 px-4 rounded focus:outline-none focus:shadow-outline"
      >
        Abrir Formulario de Aviso
      </button>
    </div>
  );
};

const RutaSoloAdministrador = ({ children }) => {
  const { user } = useAuth();
  return user?.rol === 'administrador' ? children : <Navigate to="/dashboard" replace />;
};


function App() {
  // Estado para controlar la visibilidad de la barra lateral en móviles
  const [sidebarAbierta, setSidebarAbierta] = useState(false);
  // Estado para controlar la visibilidad del modal de crear aviso
  const [modalAvisoAbierto, setModalAvisoAbierto] = useState(false);
  const [refreshAvisosDelDia, setRefreshAvisosDelDia] = useState(0); // New state variable

  const handleOpenModal = () => setModalAvisoAbierto(true);
  const handleCloseModal = () => setModalAvisoAbierto(false);

  const handleAvisoCreated = () => {
    setRefreshAvisosDelDia(prev => prev + 1); // Increment to trigger re-fetch
  };

  return (
    <GoogleOAuthProvider clientId="921362901917-v2tql4du2rqolku0as8lbtug81vp5gsc.apps.googleusercontent.com">
      <AuthProvider> {/* Envolver toda la aplicación con AuthProvider */} 
        <Router>
          <Routes>
            {/* Rutas públicas */} 
            <Route path="/iniciar-sesion" element={<IniciarSesion />} />
            <Route path="/solicitar-acceso" element={<SolicitarAcceso />} />
            <Route path="/solicitar-recuperacion" element={<SolicitarRecuperacion />} />
            <Route path="/restablecer-password" element={<RestablecerPassword />} />

            {/* Rutas privadas - protegidas por RutaPrivada */} 
            <Route element={<RutaPrivada />}>
              <Route path="/*" element={
                <div className="relative flex flex-col min-h-screen">
                  {/* Encabezado para móvil y barra de navegación horizontal para desktop */} 
                  <div className="md:flex md:items-center bg-gray-900 text-white py-1 px-4">
                    {/* Logo de texto para desktop */} 
                    <Link to="/" className="hidden md:block px-4 flex-shrink-0">
                      <span className="text-4xl font-bold text-orange-500">Saasfix</span>
                    </Link>
                    <Encabezado toggleSidebar={() => setSidebarAbierta(!sidebarAbierta)} onOpenModal={handleOpenModal} />
                    </div>

                  {/* Barra Lateral para móvil */} 
                  <div className={`bg-gray-900 text-white w-64 space-y-6 py-7 px-2 absolute inset-y-0 left-0 transform ${sidebarAbierta ? 'translate-x-0' : '-translate-x-full'} md:hidden transition duration-200 ease-in-out z-30`}>
                    {/* Aquí renderizamos el Encabezado, pero lo adaptamos para que se vea como una barra lateral */}  
                    <Encabezado toggleSidebar={() => setSidebarAbierta(false)} onOpenModal={handleOpenModal} isMobileSidebar={true} />
                  </div>

                  {/* Overlay para cerrar la barra lateral en móvil */} 
                  {sidebarAbierta && (
                    <div
                      className="fixed inset-0 bg-black opacity-50 z-20 md:hidden"
                      onClick={() => setSidebarAbierta(false)}
                    ></div>
                  )}

                  {/* Contenido Principal */} 
                  <main className="flex-grow p-6 md:p-10 pb-16"> {/* Añadido pb-16 para espacio del footer */}  
                    <Routes>
                      <Route path="/" element={<DashboardPage />} />
                      <Route path="/dashboard" element={<DashboardPage />} />
                      <Route path="/gestion-tecnicos" element={<GestionTecnicosPage />} />
                      <Route path="/avisos-del-dia" element={<AvisosDelDia refreshTrigger={refreshAvisosDelDia} />} />
                      <Route path="/crear-avisos" element={<CrearAvisos onOpenModal={handleOpenModal} />} /> {/* Pasar la función al componente CrearAvisos */}  
                      <Route path="/avisos-pendientes" element={<AvisosPendientes />} />
                      <Route path="/avisos-completados" element={<AvisosCompletados />} />
                      <Route path="/historial-servicios" element={<HistorialDeServicios />} />

                      {/* Rutas de Resumen Financiero */}  
                      <Route path="/finanzas" element={<RutaSoloAdministrador><FinancialApp /></RutaSoloAdministrador>} />
                      <Route path="/facturacion" element={<FacturacionSimple />} />
                      <Route path="/facturas" element={<ListaFacturas />} />

                      {/* Rutas de Clientes */}  
                      <Route path="/clientes" element={<GestionClientesPage />} />
                      <Route path="/clientes/:id" element={<VerClientePage />} />
                      <Route path="/clientes/editar/:id" element={<EditarClientePage />} />
                      <Route path="/clientes/buscar" element={<BuscarClientePage />} />

                      {/* Rutas de Stock */}  
                      <Route path="/inventario" element={<Inventario />} />
                    </Routes>
                  </main>

                  {/* Pie de página fijo */}  
                  <PieDePagina className="fixed bottom-0 w-full" />
                </div>
              } />
            </Route>
          </Routes>
          {/* Renderizar el modal fuera de las rutas para que siempre esté disponible */}  
          <ModalCrearAviso isOpen={modalAvisoAbierto} onClose={handleCloseModal} onAvisoCreated={handleAvisoCreated} />
        </Router>
      </AuthProvider>
    </GoogleOAuthProvider>
  );
}

export default App;